/**
 * @file CustomPatientRows.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import * as React from "react";
import TablePagination from "@mui/material/TablePagination";
import { Dialog, DialogContent, Stack, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import "./PatientHome.css"
import { PatientRow } from "../../../../services/models/PatientRow";
import { GetPatientResponse } from "../../../../services/models/GetPatientResponse";
import { RoleType } from "../../../login/utils/RoleType";
import { TablePaginationView } from "../../../common/customviews/TablePaginationView";
import { BaseRowComponent } from "../../base/BaseRowComponent";
import { BaseProps } from "../../base/BaseProps";
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";
import { ApiError } from "../../../../services/models/ApiError";
import { CustomPatientViewModel } from "../viewmodels/CustomPatientViewModel";
import { EditIdentifierView } from "../../admin/views/EditIdentifierView";
import { CustomToolTip } from "../../../common/customviews/CustomToolTip";

/**
 * An interface definition for Component Props
 */
type CompState = {
    role: RoleType, redraw: boolean,
    mainLoadding: boolean, row?: PatientRow, patientEditing?: boolean
    editingName: string, showEditDialog: boolean,
}

/**
 * An interface definition for Component Props
 */
interface Props extends BaseProps {
    viewQrCode: any;
    deletePatient: any;
    getPatientResponse: GetPatientResponse;
    clinicId?: string;
}

/**
 * A class definition for CustomPatientRows
 */
export default class CustomPatientRows extends BaseRowComponent<Props, CompState>  {

    /**
     * Loads data in paginations
     */
    protected loadMoreData() {
        this.logger.verbose('loadMoreData()', CustomPatientRows.name);
        const currentDisplayedCount = this.page * this.rowsPerPage;
        if (currentDisplayedCount >= this.getPatientResponse.rows?.length!!) { // eslint-disable-line
            this.logger.verbose('Current data end displayed.', CustomPatientRows.name);

            if (this.getPatientResponse.rows?.length!! < this.getPatientResponse.count!!) { // eslint-disable-line
                this.logger.verbose('Load new data.', CustomPatientRows.name);
                this.showMainLoading();
                this.loadPatients(this.props.clinicId!!); // eslint-disable-line
            }
        }
    }

    /**
     * Share states from parent to child
     */
    protected shareStates() {
        this.redraw();
    }

    private getPatientResponse: GetPatientResponse;

    /**
     * Force redraw
     */
    private redraw = () => {
        this.setState({
            redraw: !this.state.redraw
        });
    }

    private viewModel: CustomPatientViewModel;

    constructor(props: Props) {
        super(props)
        this.viewModel = new CustomPatientViewModel();
        this.state = {
            redraw: false, mainLoadding: false,
            role: this.getLoggedInUserRole() && this.getLoggedInUserRole().includes("HCP") ? RoleType.HCP : RoleType.BSCRep,
            editingName: '', showEditDialog: false, patientEditing: false
        }
        this.logger.error(this.getLoggedInUserRole(), CustomPatientRows.name);
        this.getPatientResponse = this.props.getPatientResponse;
        // eslint-disable-next-line
        this.emptyRows = this.page > 0 ? Math.max(0, (1 + this.page) * this.rowsPerPage - this.getPatientResponse.count!!) : 0;
    }

    /**
     * Dismisses the new clinic dialog
     */
    public dismissEditPatientDialog = () => {
        this.setState({ showEditDialog: false });
    }

    /**
     * Shows the new patient dialog
     */
    public showEditPatientDialog = (row: PatientRow) => {
        this.setState({ showEditDialog: true, row: row });
    }
    /**
     * Handles new patient callback
     */
    private onPatientEdited = () => {
        this.setState({ showEditDialog: false, patientEditing: true });
        // load the list of patient again
        this.page = 0;
        this.loadPatients(this.props.clinicId!!); // eslint-disable-line
    }

    /**
    * Subscribes to api calls
    * @returns 
    */
    private subscribeToApiCalls = () => {
        this.viewModel.onLoadedPatientList.subscribe(data => {

            if (this.state.patientEditing) {
                this.getPatientResponse = data;
                this.hideMainLoading();
                this.shareStates();
                return;
            }
            this.hideMainLoading();
            const newPatientRows = data.rows!!; // eslint-disable-line
            this.logger.verbose(`loadPatients - New Loaded Length (${newPatientRows.length})`, CustomPatientRows.name);
            const existingPatientRows = this.getPatientResponse.rows!!; // eslint-disable-line
            const totalPatients = existingPatientRows.concat(newPatientRows);

            this.getPatientResponse.rows = totalPatients;

            this.logger.verbose(`loadPatients (${JSON.stringify(totalPatients.length)})`, CustomPatientRows.name);
            this.logger.verbose(`loadPatients - Total Length (${this.getPatientResponse.rows.length})`, CustomPatientRows.name);
        });

        this.viewModel.onError.subscribe((error: ApiError) => {
            this.logger.verbose(`loadPatients (${JSON.stringify(error)})`, CustomPatientRows.name);

        });
    }

    /**
    * Loads patients
    * @param clinicId string
    * @returns 
    */
    private loadPatients = (clinicId: string) => {
        const accessToken = this.validateAndGetSession(CustomPatientRows.name);
        this.viewModel.getPatientList(clinicId, accessToken, this.page);
    }

    /**
     * LifeCycle mthod 
     */
    componentDidMount(): void {
        this.subscribeToApiCalls();
    }


    componentWillUnmount(): void {
        this.viewModel.onLoadedPatientList.unsubscribe();
        this.viewModel.onError.unsubscribe();
    }

    /**
     * Toggle main loading flag
     */
    private showMainLoading = () => this.setState({ mainLoadding: true });

    /**
     * Toggle main loading flag
     */
    private hideMainLoading = () => this.setState({ mainLoadding: false, patientEditing: false });

    private getPatientRowView = (row: PatientRow) => {
        this.index = this.index + 1;
        this.logger.error(this.index + '-' + this.page + "----", CustomPatientRows.name);
        return <TableRow className="TableRow">
            <TableCell sx={{ width: '35%' }} className="TableData">
                <Typography sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical", fontSize: "0.9rem"
                }}>
                    {row.patient?.id}
                </Typography>
            </TableCell>
            <TableCell sx={{ paddingLeft: "2vh", width: '35%' }} className="TableData">
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    marginRight={8}
                    spacing={2}
                >
                    <div style={{ maxWidth: "80%" }}>
                        <CustomToolTip sx={{ textTransform: "capitalize" }} toolTipLengthLimit={25} title={this.state.role == RoleType.HCP ? (row.patient?.identifier == null ? "NA" : row.patient?.identifier) : "***"}>
                            <Typography sx={{
                                overflow: "hidden", textTransform: "capitalize",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: "1",
                                WebkitBoxOrient: "vertical",
                                fontSize: "0.9rem"
                            }}>
                                {this.state.role == RoleType.HCP ? (row.patient?.identifier == null ? "NA" : row.patient?.identifier) : "***"}
                            </Typography>
                        </CustomToolTip>
                    </div>
                    <img className="EditIdentifier" style={{ display: this.state.role == RoleType.BSCRep ? "none" : "block", height: "2.5vh", cursor: "pointer" }} src={"/../edit.svg"}
                        onClick={() => {
                            this.logger.verbose('Onclick(Edit Patient)', CustomPatientRows.name);
                            this.showEditPatientDialog(row);
                        }} />
                </Stack>
            </TableCell>
            <TableCell sx={{ paddingLeft: "2vh", width: '30%' }} className="TableData">
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}>
                    <img className="view-qr" src="/../viewqr.svg" alt="" onClick={() => this.props.viewQrCode(row.id)} style={{ height: "2.5vh", cursor: "pointer" }} />
                    <img className="delete-patient" src="/../delete.svg" alt="" onClick={() => this.props.deletePatient(row)} style={{ height: "2.5vh", cursor: "pointer" }} />
                </Stack>
            </TableCell>
        </TableRow>
    }

    render() {
        return <React.Fragment>
            <TableBody>
                {(this.rowsPerPage > 0
                    ? this.getPatientResponse.rows!!.slice(this.page * this.rowsPerPage, this.page * this.rowsPerPage + this.rowsPerPage) //eslint-disable-line 
                    : this.getPatientResponse.rows!! //eslint-disable-line 
                ).map((row: PatientRow) => (
                    // eslint-disable-next-line
                    this.getPatientRowView(row)
                ))}
                {this.emptyRows > 0 && (
                    <TableRow style={{ height: 53 * this.emptyRows }} key={this.getRowRandomID()}>
                        <TableCell colSpan={3} />
                    </TableRow>
                )}
            </TableBody>
            <TablePagination
                rowsPerPageOptions={[10]}
                colSpan={3}
                count={this.getPatientResponse?.count!!} //eslint-disable-line 
                rowsPerPage={this.rowsPerPage}
                page={this.page}
                SelectProps={{
                    inputProps: {
                        "aria-label": "rows per page",
                    },
                    native: true,
                }}
                onPageChange={this.handleChangePage}
                onRowsPerPageChange={this.handleChangeRowsPerPage}
                ActionsComponent={TablePaginationView}
            />
            <Dialog onClose={this.dismissEditPatientDialog} aria-labelledby="customized-dialog-title" open={this.state.showEditDialog}
                maxWidth="sm" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                <DialogContent style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                    <EditIdentifierView onDialogDismissed={this.dismissEditPatientDialog} onPatientEdited={this.onPatientEdited} patientRow={this.state.row} />
                </DialogContent>
            </Dialog>

            <BackDropLoadingView handleBackDropClose={this.hideMainLoading} isLoading={this.state.mainLoadding} />

        </React.Fragment>
    }
}
